/**
 * Dutch-locale formatting for report values.
 *
 * Every helper here returns `undefined` for a value the RDW does not have, and a `DataRow` renders
 * nothing at all for `undefined`. That is the whole convention: absence travels from the API to the
 * screen without ever becoming a stray "0", "-" or empty cell.
 */

const dateFormat = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
const longDateFormat = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
const dateTimeFormat = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const numberFormat = new Intl.NumberFormat('nl-NL')
const euroFormat = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})
const priceFormat = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
})

type Maybe<T> = T | null | undefined

/** Parses the API's `DateOnly` (`2019-03-14`) and `DateTimeOffset` strings. */
function toDate(value: Maybe<string>): Date | undefined {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function formatDate(value: Maybe<string>): string | undefined {
  const date = toDate(value)
  return date && dateFormat.format(date)
}

export function formatLongDate(value: Maybe<string>): string | undefined {
  const date = toDate(value)
  return date && longDateFormat.format(date)
}

export function formatDateTime(value: Maybe<string>): string | undefined {
  const date = toDate(value)
  return date && dateTimeFormat.format(date)
}

/**
 * How long ago `value` was, in whole years or months, or `undefined` for a date in the future.
 *
 * The report is full of dates a reader has to do arithmetic on to use: "eerste toelating 26-06-2018"
 * is only useful once you have worked out that the car is seven. This does that once, so the page
 * can print the answer beside the fact rather than leaving it as homework.
 *
 * Calendar months rather than milliseconds, so a car admitted on 1 March is "7 jaar" on 1 March and
 * not the day before. Under a year it counts in months, because "0 jaar" says nothing.
 */
export function formatElapsed(value: Maybe<string>, now: Date = new Date()): string | undefined {
  const date = toDate(value)

  if (!date) {
    return undefined
  }

  let months = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth())

  if (now.getDate() < date.getDate()) {
    months -= 1
  }

  if (months < 0) {
    return undefined
  }

  const years = Math.floor(months / 12)

  if (years >= 1) {
    return `${years} jaar`
  }

  return months === 0 ? 'deze maand' : `${months} ${months === 1 ? 'maand' : 'maanden'}`
}

/** Whether `value` is a date in the past, relative to now. Used for the APK verdict. */
export function isPast(value: Maybe<string>): boolean | undefined {
  const date = toDate(value)
  return date && date.getTime() < Date.now()
}

export function formatNumber(value: Maybe<number>, unit?: string): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  const formatted = numberFormat.format(value)
  return unit ? `${formatted} ${unit}` : formatted
}

/**
 * A year, without the thousands separator `Intl.NumberFormat` would otherwise put in it — nobody
 * writes a model year as "2.023".
 */
export function formatYear(value: Maybe<number>): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  return String(value)
}

/** Reads as "7,2%" — no space before the sign, which is how Dutch writes it. */
export function formatPercent(value: Maybe<number>): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  return `${numberFormat.format(value)}%`
}

/** Formats a length the API gives in millimetres as centimetres, which is how a car is described. */
export function formatLengthCm(millimetres: Maybe<number>): string | undefined {
  if (millimetres === null || millimetres === undefined) {
    return undefined
  }

  return `${numberFormat.format(Math.round(millimetres / 10))} cm`
}

/**
 * A price, with its cents: "€ 3,99".
 *
 * Separate from `formatEuro` because that one drops the fraction — right for a catalogue price of
 * €36.790, wrong for a price tag, where "€ 4" is both inaccurate and reads as a different offer.
 */
export function formatPrice(value: number): string {
  return priceFormat.format(value)
}

export function formatEuro(value: Maybe<number>): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  return euroFormat.format(value)
}

/**
 * `Ja` / `Nee`. Unknown stays unknown — a missing flag is not a "Nee", and the difference between
 * "the RDW says this car is not exported" and "the RDW does not publish that" is real.
 */
export function formatBoolean(value: Maybe<boolean>): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  return value ? 'Ja' : 'Nee'
}

/**
 * Sentence case for the RDW's shouted colour names: `ZWART` reads as `Zwart`.
 *
 * The register stores colours in capitals, which on a page of ordinary sentences reads as
 * shouting rather than as a value. This changes the typography and nothing else — no colour is
 * renamed, merged or corrected.
 */
export function formatColour(value: Maybe<string>): string | undefined {
  if (!value) {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/** Joins a list into a readable Dutch enumeration, or returns `undefined` when it is empty. */
export function formatList(values: Maybe<readonly string[]>): string | undefined {
  const items = values?.filter(Boolean)
  return items?.length ? items.join(', ') : undefined
}

const GEARBOX_LABELS: Record<string, string> = {
  Automatic: 'Automaat',
  Manual: 'Handgeschakeld',
  Continuous: 'Traploos (CVT)',
}

/** Translates the API's gearbox enum. An unrecognised value is passed through rather than hidden. */
export function formatGearbox(value: Maybe<string>): string | undefined {
  if (!value) {
    return undefined
  }

  return GEARBOX_LABELS[value] ?? value
}

/**
 * Reads as "1.998 cc (2,0 liter)". The litre figure is the one people recognise; the exact
 * displacement is what the RDW registered, so both are shown.
 */
export function formatDisplacement(cc: Maybe<number>): string | undefined {
  if (cc === null || cc === undefined) {
    return undefined
  }

  return `${numberFormat.format(cc)} cc (${(cc / 1000).toFixed(1).replace('.', ',')} liter)`
}

/** Reads as "135 kW (184 pk)". Power is quoted in pk in the Netherlands but registered in kW. */
export function formatPower(kw: Maybe<number>, hp: Maybe<number>): string | undefined {
  if (kw === null || kw === undefined) {
    return hp === null || hp === undefined ? undefined : `${numberFormat.format(hp)} pk`
  }

  const kilowatts = `${numberFormat.format(kw)} kW`
  return hp === null || hp === undefined ? kilowatts : `${kilowatts} (${numberFormat.format(hp)} pk)`
}

/** Reads as "5,4 l/100 km". */
export function formatConsumption(litresPer100Km: Maybe<number>): string | undefined {
  if (litresPer100Km === null || litresPer100Km === undefined) {
    return undefined
  }

  return `${numberFormat.format(litresPer100Km)} l/100 km`
}

/**
 * The car's headline name: "Peugeot Partner", falling back to whichever half the RDW published.
 *
 * The register stores both in capitals. On the report's own heading that does not matter, because
 * the heading is uppercased in CSS anyway, but this string also goes into the `<title>`, the meta
 * description and the generated summary paragraph, and "PEUGEOT PARTNER" in a search result reads
 * as shouting rather than as a name.
 *
 * A word is only lowered when it is four or more letters and nothing but letters, which leaves the
 * initialisms alone: `BMW`, `KIA`, `MG` and `DS` stay as they are, and so does a trim code like
 * `320E`, because it is not all letters. Hyphenated names split too, so `MERCEDES-BENZ` comes back
 * as `Mercedes-Benz`.
 *
 * Some manufacturers register the brand inside the trade name as well (`TOYOTA` + `TOYOTA AYGO`),
 * so the brand is only prefixed when the trade name does not already start with it as a whole word.
 * Without that the heading, the `<title>` and the summary all read "Toyota Toyota Aygo".
 */
export function vehicleName(brand?: string, tradeName?: string): string {
  const name = startsWithWord(tradeName, brand) ? tradeName : [brand, tradeName].filter(Boolean).join(' ')
  return formatModelName(name) ?? 'Onbekend voertuig'
}

function startsWithWord(value: string | undefined, prefix: string | undefined): boolean {
  if (!value || !prefix) return false
  const head = value.trim().toLowerCase()
  const word = prefix.trim().toLowerCase()
  return head === word || head.startsWith(`${word} `)
}

/**
 * One make or model name out of the register, cased for reading: `PEUGEOT` becomes `Peugeot`.
 *
 * The same treatment `formatColour` gives colours, and for the same reason: the register stores
 * these in capitals, and a table of ordinary sentence-case values with `PEUGEOT` in it reads as
 * shouting rather than as a value. It also keeps the rows agreeing with the heading and the
 * `<title>`, which are cased the same way.
 */
export function formatModelName(value: Maybe<string>): string | undefined {
  return value ? value.replace(/[^\s-]+/g, capitaliseWord) : undefined
}

/**
 * Capitalises the first letter and leaves the rest alone: `gesloten opbouw` becomes
 * `Gesloten opbouw`.
 *
 * Deliberately not `formatColour`'s lower-casing of the remainder, which would turn `MPV` into
 * `Mpv`. The RDW writes some of these fields in lower case and some in capitals, and the only safe
 * change is the first character.
 */
export function sentenceCase(value: Maybe<string>): string | undefined {
  if (!value) {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

function capitaliseWord(word: string): string {
  if (word.length < 4 || !/^[a-zA-Z]+$/.test(word)) {
    return word
  }

  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

/** Turns a section title into a stable anchor id for the quick-jump navigation. */
export function toAnchorId(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}
